import { useState } from 'react'
import { t } from '@lingui/core/macro'
import { FlashOn as FlashOnIcon } from '@mui/icons-material'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsAutoComplete, CvsButton } from '@components'
import { TextField } from '@components/formFields/textField'
import { useAppSelector } from '@redux/hooks'
import { PoweredDatasetModal } from '../../powered-dataset-modal'
import { ListType, type List, type ListTypeOption } from '../types'

interface ListFormState {
  name: string
  description: string
  type: ListType
}

interface ListAttributesFormProps {
  list: List
  onPoweredSave: (data: { template: string; column: string }) => Promise<void>
  state: ListFormState
  onChange: <K extends keyof ListFormState>(attribute: K, value: ListFormState[K]) => void
  isApproverMode: boolean
}

export const ListAttributesForm = ({
  list,
  state,
  onChange,
  onPoweredSave,
  isApproverMode,
}: ListAttributesFormProps) => {
  const [powerDatasetModal, setPowerDatasetModal] = useState(false)
  const listTypeOptions: ListTypeOption[] = [
    { label: t`Drop Down`, value: 'drop-down' },
    { label: t`Drill Down`, value: 'drill-down' },
    { label: t`Dependent`, value: 'dependent' },
  ]

  const [DATASETS_ENABLED = false, LISTS_AS_DATASETS = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE, FeatureDict.LISTS_AS_DATASETS]),
  )

  return (
    <>
      <div style={{ flex: 1, paddingRight: 24 }}>
        <TextField
          variant="outlined"
          margin="dense"
          required={true}
          value={state.name}
          setValueCallback={value => {
            onChange('name', value)
          }}
          name="name"
          label={t`List name`}
          style={{ margin: 0, width: '100%' }}
        />
      </div>
      <div style={{ flex: 1, paddingRight: 24 }}>
        <TextField
          variant="outlined"
          margin="dense"
          value={state.description}
          setValueCallback={value => {
            onChange('description', value)
          }}
          name="description"
          label={t`List description`}
          style={{ margin: 0, width: '100%' }}
        />
      </div>
      <div style={{ flex: 1, paddingRight: 24 }}>
        <CvsAutoComplete
          variant="outlined"
          margin="dense"
          selected={state.type}
          onSelected={(val: ListType) => {
            if (list.powered_template_id) {
              setPowerDatasetModal(true)
            }
            onChange('type', val)
          }}
          name="type"
          classNamePrefix="list-type"
          label={t`List type`}
          options={listTypeOptions as never[]}
          style={{ margin: 0, width: '100%' }}
        />
      </div>
      {isApproverMode && (
        <div style={{ paddingRight: 24 }}>
          {LISTS_AS_DATASETS && (
            <CvsButton
              variant="contained"
              size="large"
              color="primary"
              startIcon={<FlashOnIcon />}
              onClick={() => {
                setPowerDatasetModal(true)
              }}
            >
              {list.powered_template_id
                ? DATASETS_ENABLED
                  ? t`Adjust Powered Dataset`
                  : t`Adjust Powered Template`
                : DATASETS_ENABLED
                  ? t`Power with Dataset`
                  : t`Power with Template`}
            </CvsButton>
          )}
        </div>
      )}
      <PoweredDatasetModal
        open={powerDatasetModal}
        setOpen={setPowerDatasetModal}
        singleColumn={state.type === 'drop-down'}
        save={onPoweredSave}
        initialTemplate={list.powered_template_id}
      />
    </>
  )
}
